/**
 * Corner debug panel: FPS, frame time, scene counts.
 * Click to collapse; press backtick (`) to toggle visibility.
 */
export class PerfHud {
  readonly root: HTMLDivElement;
  private readonly body: HTMLPreElement;
  private visible = true;
  private collapsed = false;
  private frames = 0;
  private fps = 0;
  private frameMs = 0;
  private fpsWindowStart = performance.now();
  private readonly onKey: (event: KeyboardEvent) => void;

  constructor(parent: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "perf-hud";
    this.root.title = "Click to collapse · ` to hide";

    const title = document.createElement("div");
    title.className = "perf-hud-title";
    title.textContent = "perf";

    this.body = document.createElement("pre");
    this.body.className = "perf-hud-body";

    this.root.append(title, this.body);
    parent.appendChild(this.root);

    this.root.addEventListener("click", () => {
      this.collapsed = !this.collapsed;
      this.root.classList.toggle("is-collapsed", this.collapsed);
    });

    this.onKey = (event: KeyboardEvent) => {
      if (event.key === "`" && !event.metaKey && !event.ctrlKey && !event.altKey) {
        const target = event.target as HTMLElement | null;
        if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
          return;
        }
        this.visible = !this.visible;
        this.root.hidden = !this.visible;
      }
    };
    window.addEventListener("keydown", this.onKey);
  }

  tick(frameDeltaMs: number, stats: PerfStats): void {
    this.frames += 1;
    this.frameMs = frameDeltaMs;
    const now = performance.now();
    if (now - this.fpsWindowStart >= 500) {
      this.fps = (this.frames * 1000) / (now - this.fpsWindowStart);
      this.frames = 0;
      this.fpsWindowStart = now;
    }

    if (!this.visible || this.collapsed) {
      return;
    }

    const mem =
      "memory" in performance
        ? (() => {
            const m = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
            return m ? `${(m.usedJSHeapSize / 1048576).toFixed(1)} MB` : "n/a";
          })()
        : "n/a";

    const fpsColor = this.fps >= 50 ? "#9ccc65" : this.fps >= 30 ? "#ffe082" : "#ef9a9a";

    this.body.innerHTML =
      `<span style="color:${fpsColor}">${this.fps.toFixed(0).padStart(3, " ")} fps</span>  ${this.frameMs.toFixed(1).padStart(4, " ")} ms\n` +
      `dogs ${String(stats.dogs).padStart(3, " ")}  critters ${String(stats.critters).padStart(3, " ")}\n` +
      `draw ${String(stats.drawCalls).padStart(4, " ")}  tris ${formatCount(stats.triangles)}\n` +
      `geo  ${String(stats.geometries).padStart(4, " ")}  tex  ${String(stats.textures).padStart(4, " ")}\n` +
      `pos  ${stats.localX.toFixed(2)}, ${stats.localY.toFixed(2)}\n` +
      `zone ${stats.zone}\n` +
      `heap ${mem}`;
  }

  destroy(): void {
    window.removeEventListener("keydown", this.onKey);
    this.root.remove();
  }
}

export interface PerfStats {
  dogs: number;
  critters: number;
  drawCalls: number;
  triangles: number;
  geometries: number;
  textures: number;
  localX: number;
  localY: number;
  zone: string;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1)}M`;
  }
  if (n >= 1000) {
    return `${(n / 1000).toFixed(1)}k`;
  }
  return String(n).padStart(5, " ");
}
